import React from "react";
import styles from "./smartFilterAppliedPills.module.scss";
import useSmartFilter from "../useSmartFilter";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { getPricingCurrency } from "@Pages/Search/components/SearchListItem/helper";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { COUNTRIES_WITH_MONTHLY_DURATION, PAGE } from "constants/configConstants";
import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
  STAY_DURATION_COMMON,
  STAY_DURATION_US,
} from "@Pages/Search/components/FilterSection/data/filtersList";
import useTranslationHook from "hooks/useTranslationHook";
import { translateFilterOptionLabel } from "@Pages/Search/utils/getFilterLabel";
import { formatAmenityName } from "@Pages/Search/components/FilterSection/components/Filters/AmenitiesFilter/utils/formatAmenityName";
import { CloseIcon } from "../icons";

/**
 * Builds a value → display name lookup from the room type filter lists.
 */
const ROOM_TYPE_LABEL_MAP: Record<string, string> = [
  ...ROOM_TYPE_SPECIFIC.list,
  ...ROOM_TYPE_FILTER.list,
  ...SHARING_OPTIONS_BATHROOM.list,
  ...SHARING_OPTIONS_KITCHEN.list,
].reduce((map, item) => {
  map[item.value] = item.name;
  return map;
}, {} as Record<string, string>);

/**
 * Formats the display value for a filter pill.
 * Budget is formatted as "£32 – £400" with currency symbol.
 * Room types are mapped to their human-readable labels.
 */
const formatPillValue = (
  key: string,
  value: any,
  currency: string,
  sortOrder: string | undefined,
  t: (key: string, options?: any) => string,
): string => {
  if (key === "range_available_price" && typeof value === "string") {
    const [min, max] = value.split(",");
    if (min && max) return `${currency}${min} – ${currency}${max}`;
    if (min && !max) return `${currency}${min}+`;
    if (!min && max) return t("searchpage.components.smartfilters.up_to_price", { currency, max });
  }
  if (key === "meta_types" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) => translateFilterOptionLabel(ROOM_TYPE_LABEL_MAP[v.trim()] || v.trim(), t))
      .join(", ");
  }
  if (key === "move_in_month" && typeof value === "string") {
    // Convert "sep-2026" to "Sep, 2026"
    const [month, year] = value.split("-");
    if (month && year) {
      return `${month.charAt(0).toUpperCase()}${month.slice(1)}, ${year}`;
    }
  }
  if (key === "sort_key" && typeof value === "string") {
    const sortCombo = `${value},${sortOrder || "desc"}`;
    const sortLabelMap: Record<string, string> = {
      "available_price,asc": t("searchpage.components.sortoptions.pricelowtohigh"),
      "available_price,desc": t("searchpage.components.sortoptions.pricehightolow"),
      "distance,asc": t("searchpage.components.sortoptions.nearest"),
      "distance,desc": t("searchpage.components.sortoptions.nearest"),
      "created,desc": t("searchpage.components.sortoptions.newlyadded"),
      "created,asc": t("searchpage.components.sortoptions.newlyadded"),
    };
    return sortLabelMap[sortCombo] || value;
  }
  if (key === "tags" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) => translateFilterOptionLabel(formatAmenityName(v.trim()), t))
      .join(", ");
  }
  if (key === "region_radius") {
    const meters = Number(value);
    const unitMile = t("searchpage.components.smartfilters.unit_mile");
    const unitMiles = t("searchpage.components.smartfilters.unit_miles");
    if (meters <= 1609)
      return t("searchpage.components.smartfilters.up_to_miles", { count: "1", unit: unitMile });
    if (meters <= 3218)
      return t("searchpage.components.smartfilters.up_to_miles", { count: "2", unit: unitMiles });
    if (meters <= 8046)
      return t("searchpage.components.smartfilters.up_to_miles", { count: "5", unit: unitMiles });
    return t("searchpage.components.smartfilters.up_to_miles", {
      count: String(Math.round(meters / 1609)),
      unit: unitMiles,
    });
  }
  if (key === "amenities" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) => translateFilterOptionLabel(formatAmenityName(v.trim()), t))
      .join(", ");
  }
  if (key === "meta_unit_types" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) => translateFilterOptionLabel(formatAmenityName(v.trim()), t))
      .join(", ");
  }
  return String(value);
};

const SmartFilterAppliedPills: React.FC = () => {
  const { appliedFilters, removeFilter } = useSmartFilter();
  const { t } = useTranslationHook();
  const region = useSelector(({ searchPageData }: AppState) => searchPageData.region);
  const regionCurrency = getCurrencyFromRegion(region?.combined_name);
  const currency = getPricingCurrency(regionCurrency) || "£";

  const getFilterCategoryLabel = (key: string): string => {
    const categoryMap: Record<string, string> = {
      sort_key: t("searchpage.components.smartfilters.filter_category.sort"),
      range_available_price: t("searchpage.components.smartfilters.filter_category.budget"),
      meta_types: t("searchpage.components.smartfilters.filter_category.room_type"),
      meta_unit_types: t("searchpage.components.smartfilters.filter_category.property_type"),
      move_in_month: t("searchpage.components.smartfilters.filter_category.move_in"),
      meta_min_lease_duration: t("searchpage.components.smartfilters.filter_category.min_lease"),
      meta_max_lease_duration: t("searchpage.components.smartfilters.filter_category.max_lease"),
      tags: t("searchpage.components.smartfilters.filter_category.tags"),
      amenities: t("searchpage.components.smartfilters.filter_category.amenities"),
      guarantor: t("searchpage.components.smartfilters.filter_category.guarantor"),
      university: t("searchpage.components.smartfilters.filter_category.university"),
      locality: t("searchpage.components.smartfilters.filter_category.locality"),
      region_radius: t("searchpage.components.smartfilters.filter_category.distance"),
      available: t("searchpage.components.smartfilters.filter_category.availability"),
    };
    return categoryMap[key] || key;
  };

  if (!appliedFilters) return null;

  // Combine min/max lease duration into a single entry
  const hasMinLease = !!appliedFilters.meta_min_lease_duration;
  const hasMaxLease = !!appliedFilters.meta_max_lease_duration;
  const hasLeaseDuration = hasMinLease || hasMaxLease;

  const pillEntries = Object.entries(appliedFilters).filter(
    ([key, value]) =>
      value &&
      key !== "is_irrelevant" &&
      key !== "sort_order" &&
      key !== "university_canonical_name" &&
      key !== "locality_canonical_name" &&
      key !== "meta_min_lease_duration" &&
      key !== "meta_max_lease_duration",
  );

  if (pillEntries.length === 0 && !hasLeaseDuration) return null;

  const handleRemoveLeaseDuration = () => {
    if (hasMinLease) removeFilter("meta_min_lease_duration");
    if (hasMaxLease) removeFilter("meta_max_lease_duration");
    gtagEventEmitter({
      action: "smart_filter_pill_removed",
      category: PAGE.SEARCH,
      label: "lease_duration",
    });
  };

  const getLeaseDurationLabel = (): string => {
    const min = appliedFilters.meta_min_lease_duration || "0";
    const max = appliedFilters.meta_max_lease_duration || "";
    const countryShortName = region?.location?.country?.short_name || "";
    const isMonthly = COUNTRIES_WITH_MONTHLY_DURATION.includes(countryShortName);

    const unit = isMonthly ? "months" : "weeks";
    const durationOptions = isMonthly ? STAY_DURATION_US : STAY_DURATION_COMMON;
    const matchValue = `${min} ${unit},${max ? `${max} ${unit}` : ""}`;

    const matched = durationOptions.find((opt) => opt.value === matchValue);
    if (matched && matched.name !== "All") return translateFilterOptionLabel(matched.name, t);

    // Fallback
    const fallbackUnit = isMonthly
      ? t("searchpage.components.smartfilters.unit_months")
      : t("searchpage.components.smartfilters.unit_weeks");
    if (max) return `${min} - ${max} ${fallbackUnit}`;
    return `${min}+ ${fallbackUnit}`;
  };

  const leaseDurationCategory = t(
    "searchpage.components.smartfilters.filter_category.lease_duration",
  );

  return (
    <div className={styles.container}>
      {hasLeaseDuration && (
        <div className={styles.pill}>
          <span className={styles.pillLabel}>
            {leaseDurationCategory} : {getLeaseDurationLabel()}
          </span>
          <span
            className={styles.pillClose}
            role="button"
            tabIndex={0}
            aria-label={t("searchpage.components.smartfilters.aria_remove_filter", {
              filter: leaseDurationCategory,
            })}
            onClick={handleRemoveLeaseDuration}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleRemoveLeaseDuration();
            }}
          >
            <CloseIcon />
          </span>
        </div>
      )}
      {pillEntries.map(([key, value]) => {
        const categoryName = getFilterCategoryLabel(key);
        return (
          <div key={key} className={styles.pill}>
            <span className={styles.pillLabel}>
              {categoryName} : {formatPillValue(key, value, currency, appliedFilters.sort_order, t)}
            </span>
            <span
              className={styles.pillClose}
              role="button"
              tabIndex={0}
              aria-label={t("searchpage.components.smartfilters.aria_remove_filter", {
                filter: categoryName,
              })}
              onClick={() => {
                removeFilter(key);
                gtagEventEmitter({
                  action: "smart_filter_pill_removed",
                  category: PAGE.SEARCH,
                  label: key,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  removeFilter(key);
                  gtagEventEmitter({
                    action: "smart_filter_pill_removed",
                    category: PAGE.SEARCH,
                    label: key,
                  });
                }
              }}
            >
              <CloseIcon />
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default wrapperHOC(SmartFilterAppliedPills, {
  componentName: "SmartFilterAppliedPills",
  showForChina: false,
  showForWhiteLable: true,
});
